import { useState, useCallback, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { navigation } from '@data/siteData';
import { trackNavigationClick } from '../../lib/analytics';

interface NavItem {
  title: string;
  url: string;
  submenu?: NavItem[];
}

function NavLink({ 
  item, 
  mobile = false, 
  onNavigate,
  onCloseMobileMenu 
}: { 
  item: NavItem; 
  mobile?: boolean; 
  onNavigate?: (title: string, url: string) => void;
  onCloseMobileMenu?: () => void;
}) {
  const location = useLocation();
  const isActive = location.pathname === item.url || location.pathname.startsWith(item.url + '/');
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  
  const handleClick = useCallback(() => {
    onNavigate?.(item.title, item.url);
  }, [item.title, item.url, onNavigate]);

  // Handle keyboard navigation for dropdown
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!item.submenu) return;

    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        buttonRef.current?.focus();
        break;
      case 'ArrowDown':
        if (!mobile && !isOpen) {
          e.preventDefault();
          setIsOpen(true);
        }
        break;
      case 'ArrowUp':
        if (!mobile && isOpen) {
          e.preventDefault();
          setIsOpen(false);
        }
        break;
    }
  };

  // Focus trap for dropdown items
  useEffect(() => {
    if (!isOpen || mobile) return;

    const handleDropdownKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const dropdown = dropdownRef.current;
      if (!dropdown) return;

      const focusableElements = dropdown.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled])'
      );
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    document.addEventListener('keydown', handleDropdownKeyDown);
    return () => document.removeEventListener('keydown', handleDropdownKeyDown);
  }, [isOpen, mobile]);

  if (item.submenu) {
    return (
      <div
        className={`relative ${mobile ? 'w-full' : ''}`}
        onMouseEnter={() => !mobile && setIsOpen(true)}
        onMouseLeave={() => !mobile && setIsOpen(false)}
      >
        <button
          ref={buttonRef}
          onClick={() => mobile && setIsOpen(!isOpen)}
          onKeyDown={handleKeyDown}
          className={`flex items-center gap-1 px-3 py-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
            isActive
              ? 'text-blue-400'
              : 'text-slate-200 hover:text-white hover:bg-slate-800'
          } ${mobile ? 'w-full justify-between' : ''}`}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-controls={isOpen ? `submenu-${item.url}` : undefined}
        >
          <span>{item.title}</span>
          <ChevronDown
            size={16}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
            aria-hidden="true"
          />
        </button>
        {(isOpen || mobile) && (
          <div
            ref={dropdownRef}
            id={`submenu-${item.url}`}
            role="menu"
            aria-label={`${item.title} Untermenü`}
            className={`${
              mobile
                ? 'pl-4 mt-1 space-y-1'
                : 'absolute top-full left-0 mt-1 w-64 bg-slate-900 rounded-md shadow-lg border border-slate-800 py-2 z-50'
            }`}
          >
            {(mobile || isOpen) &&
              item.submenu.map((subItem: NavItem) => (
                <Link
                  key={subItem.url}
                  to={subItem.url}
                  role="menuitem"
                  tabIndex={isOpen || mobile ? 0 : -1}
                  onClick={() => {
                    setIsOpen(false);
                    onCloseMobileMenu?.();
                    onNavigate?.(subItem.title, subItem.url);
                  }}
                  className={`block px-4 py-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-inset ${
                    location.pathname === subItem.url
                      ? 'text-blue-400 bg-slate-800'
                      : 'text-slate-300 hover:text-white hover:bg-slate-800'
                  }`}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      setIsOpen(false);
                      buttonRef.current?.focus();
                    }
                  }}
                >
                  {subItem.title}
                </Link>
              ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.url}
      onClick={handleClick}
      className={`px-3 py-2 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 ${
        isActive
          ? 'text-blue-400 font-medium'
          : 'text-slate-200 hover:text-white hover:bg-slate-800'
      } ${mobile ? 'block w-full' : ''}`}
    >
      {item.title}
    </Link>
  );
}

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const mobileMenuButtonRef = useRef<HTMLButtonElement>(null);
  
  const handleNavigate = useCallback((title: string, url: string) => {
    trackNavigationClick(title, url);
  }, []);

  const handleCloseMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Handle Escape key to close mobile menu
  useEffect(() => {
    if (!mobileMenuOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setMobileMenuOpen(false);
        // Return focus to menu button
        mobileMenuButtonRef.current?.focus();
      }
    };

    // Focus trap for mobile menu
    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      const menu = mobileMenuRef.current;
      if (!menu) return;

      const focusableElements = menu.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
      );
      
      if (focusableElements.length === 0) return;

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === firstElement) {
        e.preventDefault();
        lastElement?.focus();
      } else if (!e.shiftKey && document.activeElement === lastElement) {
        e.preventDefault();
        firstElement?.focus();
      }
    };

    const keyHandler = (e: KeyboardEvent) => {
      handleKeyDown(e);
      handleTabKey(e);
    };

    document.addEventListener('keydown', keyHandler);
    
    // Focus first element when menu opens
    const timer = setTimeout(() => {
      const firstFocusable = mobileMenuRef.current?.querySelector<HTMLElement>(
        'a[href], button:not([disabled])'
      );
      firstFocusable?.focus();
    }, 0);

    return () => {
      document.removeEventListener('keydown', keyHandler);
      clearTimeout(timer);
    };
  }, [mobileMenuOpen]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  return (
    <header 
      className="bg-slate-900/95 backdrop-blur-sm border-b border-slate-800 sticky top-0 z-50"
      role="banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900 rounded-lg"
            aria-label="Sternwarte Mannheim - Zur Startseite"
          >
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center" aria-hidden="true">
              <span className="text-white font-bold text-lg">S</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-white font-semibold text-lg leading-tight">
                Sternwarte
              </h1>
              <p className="text-slate-400 text-xs">Mannheim</p>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav 
            className="hidden lg:flex items-center gap-1" 
            role="navigation" 
            aria-label="Hauptnavigation"
          >
            {navigation.map((item) => (
              <NavLink 
                key={item.url} 
                item={item} 
                onNavigate={handleNavigate}
                onCloseMobileMenu={handleCloseMobileMenu}
              />
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <button
            ref={mobileMenuButtonRef}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-md text-slate-300 hover:text-white hover:bg-slate-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {mobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div 
          ref={mobileMenuRef}
          id="mobile-menu"
          className="lg:hidden bg-slate-900 border-t border-slate-800 fixed inset-x-0 top-16 bottom-0 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile Navigation"
        >
          <nav 
            className="max-w-7xl mx-auto px-4 py-4 space-y-2" 
            role="navigation" 
            aria-label="Mobile Navigation"
          >
            {navigation.map((item) => (
              <NavLink 
                key={item.url} 
                item={item} 
                mobile 
                onCloseMobileMenu={handleCloseMobileMenu}
              />
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}
